// src/features/commission/CommissionWorkspacePage.tsx
import { useParams } from 'react-router-dom';

import { LoadingBoundary, PageContainer, PageProgress } from '../../app/components/foundation';
import {
  CommissionWorkspaceHeader,
  CommissionInfoCard,
  CommissionCustomerCard,
  CommissionOrderCard,
} from './components';
import { useCommission } from './hooks/useCommission';
import './styles/commission.css';

/**
 * Commission Workspace Foundation (/commission/:id). Same shell
 * pattern as OrderWorkspacePage: PageContainer + feature-owned
 * header, card grid below, RTL/Persian throughout, existing
 * tokens/primitives only.
 *
 * Reads `id` from the route params and resolves everything through
 * useCommissionWorkspaceMock(id) — the commission record plus its
 * payment/timeline, all still mock data from data/commission.mock.ts.
 * No API calls; useCommissionWorkspaceMock is the seam a later phase
 * swaps for a real commission-detail hook without touching this file
 * or the cards below it (same shape).
 */
export function CommissionWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const { commission, loading, error } = useCommission(id);

  if (loading && !commission) return <PageContainer><LoadingBoundary loading /></PageContainer>;
  if (error) return <PageContainer><LoadingBoundary loading={false} error={error} /></PageContainer>;

  if (!commission) {
    return (
      <PageContainer className="flex flex-col gap-6">
        <p className="vania-commission-workspace-empty">کمیسیون مورد نظر یافت نشد.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="flex flex-col gap-6">
      <PageProgress active={loading} />
      <CommissionWorkspaceHeader commission={commission} />

      <div className="vania-commission-workspace-grid">
        <CommissionInfoCard commission={commission} />
        <CommissionCustomerCard commission={commission} />
        <CommissionOrderCard commission={commission} />
      </div>
    </PageContainer>
  );
}
